import xregexp from 'xregexp'

import { AnnotationSetType, AnnotationType, SentenceType } from '../../types/types'
import { AnnotatedSegment } from './AnnotatedSegment'
import { mergeAnnotationStyles, parseAnnotationStyle, pickLayoutStyle, serializeAnnotationStyle } from './annotationStyle'
import { isCantillation, isVowel, normalizeSentenceText, WordSplitCharRegEx } from './CharacterClasses'

class AnnotatedSentence {
  private xsentence: SentenceType

  private internalNormalizedText: string

  private cantillations: boolean

  private vowels: boolean

  private annotations: Array<AnnotationType>

  private segments: Array<AnnotatedSegment>

  private segmentBoundaries: Array<string | null>

  private segmentations: Array<number>

  private showEndOfSentenceCharacter: boolean

  private updating: boolean

  // Maps stripped text index → normalized text index.
  // Built during transformedSentence() when vowels/cantillation are stripped.
  private strippedToNormalizedMap: number[]

  public style: CSSStyleDeclaration

  constructor(
    sentence: SentenceType,
    annotationSet: AnnotationSetType | null = null,
    segmentation = [] as Array<number>,
    showEndOfSentenceCharacter = false,
  ) {
    this.xsentence = sentence

    this.segmentations = segmentation ? [...segmentation].map((s) => +s) : []

    this.internalNormalizedText = ''
    this.showEndOfSentenceCharacter = showEndOfSentenceCharacter
    this.vowels = true
    this.cantillations = true
    this.updating = false
    this.strippedToNormalizedMap = []
    this.annotations = new Array<AnnotationType>()
    if (annotationSet) {
      this.annotations = [...annotationSet.annotations]
    }

    this.segments = new Array<AnnotatedSegment>()
    this.segmentBoundaries = new Array<string>()
    this.style = {} as CSSStyleDeclaration
    this.generate()
  }

  sentence(): SentenceType {
    return this.xsentence
  }

  normalizedText(): string {
    return this.internalNormalizedText
  }

  isUpdating(): boolean {
    return this.updating
  }

  nSegments(): number {
    return this.segments.length
  }

  nWords(): number {
    let n = 0
    this.segments.forEach((annotatedSegment: AnnotatedSegment) => {
      n += annotatedSegment.nWords()
    })
    return n
  }

  set(args: { vowels: boolean; cantillations: boolean }): void {
    this.vowels = args.vowels
    this.cantillations = args.cantillations
    this.generate()
  }

  setVowels(v: boolean): void {
    this.vowels = v
    this.generate()
  }

  setCantillations(v: boolean): void {
    this.cantillations = v
    this.generate()
  }

  setIsUpdating(u: boolean): void {
    this.updating = u
  }

  isEmpty(): boolean {
    return !this.xsentence
  }

  /**
   * Map a character index in the stripped (displayed) text back to its index
   * in the full normalized text. If no stripping occurred, returns identity.
   */
  normalizedIndex(strippedIndex: number): number {
    if (!this.strippedToNormalizedMap.length) {
      return strippedIndex
    }
    return this.strippedToNormalizedMap[strippedIndex] ?? strippedIndex
  }

  /**
   * Strip characters matching `shouldStrip` from text while keeping annotations in sync.
   * When a stripped character has a newlineAfter annotation, carries it to the previous
   * surviving character so line breaks aren't lost when vowels/cantillation are removed.
   */
  private static stripChars(
    text: string,
    annotations: Array<AnnotationType | null>,
    shouldStrip: (char: string) => boolean,
  ): { text: string; annotations: Array<AnnotationType | null>; indexMap: number[] } {
    let newT = ''
    const newA: Array<AnnotationType | null> = []
    const indexMap: number[] = [] // indexMap[strippedPos] = inputPos
    ;[...text].forEach((char, index) => {
      if (!shouldStrip(char)) {
        indexMap.push(index)
        newT += char
        newA.push(annotations[index])
      } else {
        // Carry newlineAfter/stretchAfter from stripped chars to the previous surviving character
        const ann = annotations[index]
        const layoutStyle = pickLayoutStyle(parseAnnotationStyle(ann?.style))
        if (ann && newA.length > 0 && Object.keys(layoutStyle).length > 0) {
          const prev = newA[newA.length - 1]
          const prevStyle = mergeAnnotationStyles(parseAnnotationStyle(prev?.style), layoutStyle)
          newA[newA.length - 1] = { ...(prev ?? ann), style: serializeAnnotationStyle(prevStyle) }
        }
      }
    })
    return { annotations: newA, indexMap, text: newT }
  }

  transformedSentence(): {
    text: string
    annotations: Array<AnnotationType | null>
  } {
    let text = normalizeSentenceText(this.xsentence.text!, {
      keepSofPasuq: this.showEndOfSentenceCharacter,
    })

    this.internalNormalizedText = text

    // annotations is an array of nulls, except for those locations where an
    // annotation has been defined by the teacher
    let annotations = new Array<AnnotationType | null>()
    let matchedAnnotations = 0
    ;[...text].forEach((__, index) => {
      const annotation = this.annotations.find((a) => a.index === index)
      if (annotation) {
        matchedAnnotations++
      }
      annotations.push(annotation ?? null)
    })

    if (this.annotations.length > 0) {
      const newlineAnnotations = this.annotations.filter((a) => parseAnnotationStyle(a.style).newlineAfter)
      if (newlineAnnotations.length > 0 || matchedAnnotations !== this.annotations.length) {
        const annotationIndices = this.annotations.slice(0, 5).map((a) => `${a.index}(${typeof a.index})`)
        console.log(
          `AnnotatedSentence.transformedSentence [${this.xsentence.id}]: textLen=${text.length}, ${this.annotations.length} annotations, ${matchedAnnotations} matched by index, ${newlineAnnotations.length} have newlineAfter`,
          `\n  first annotation indices: [${annotationIndices.join(', ')}]`,
        )
      }
    }

    // Build composed index map: stripped position → normalized text position.
    // Each strip pass produces its own map; compose them so the final map
    // goes directly from the doubly-stripped text back to the normalized text.
    let composedMap: number[] | null = null

    if (!this.cantillations) {
      let cantMap: number[]
      ;({ annotations, indexMap: cantMap, text } = AnnotatedSentence.stripChars(text, annotations, isCantillation))
      composedMap = cantMap
    }

    if (!this.vowels) {
      let vowelMap: number[]
      ;({ annotations, indexMap: vowelMap, text } = AnnotatedSentence.stripChars(text, annotations, isVowel))
      if (composedMap) {
        // Compose: vowelMap maps stripped→post-cantillation, composedMap maps post-cantillation→normalized
        vowelMap = vowelMap.map((postCantIdx) => composedMap![postCantIdx])
      }
      composedMap = vowelMap
    }

    this.strippedToNormalizedMap = composedMap ?? []

    return { annotations, text }
  }

  generate(): void {
    const { annotations, text } = this.transformedSentence()
    this.segments.splice(0, this.segments.length)
    this.segmentBoundaries.splice(0, this.segmentBoundaries.length)

    let chars = ''
    let boundaryChars = ''
    let segmentStartIndex = 0
    let segmentCounter = 0
    let wordCount = 0
    const characterAnnotations = new Array<AnnotationType | null>()
    for (let i = 0; i < text.length; i += 1) {
      // stumbled on a split character; if so, do we actually split it into
      // a word? if no segmentations have defined, the answer is always yes.
      // otherwise, we only split the word if the boundary number matches.
      let newSegment = false
      if (xregexp.match(text[i], WordSplitCharRegEx)) {
        boundaryChars += text[i]
      } else {
        if (boundaryChars) {
          wordCount += 1

          // no segmentations, then life is simple: every split character introduces a new segment
          if (!this.segmentations.length) {
            newSegment = true

            // if we are using segmentations, then only given segmentations introduce a new segment
          } else if (wordCount >= this.segmentations[segmentCounter]) {
            newSegment = true
            segmentCounter += 1
            wordCount = 0
          }
        }

        if (newSegment) {
          const annotationsForThisSegment = [...characterAnnotations]
          const annotatedSegment = new AnnotatedSegment(this, this.segments.length, segmentStartIndex, chars, annotationsForThisSegment)
          // this.$log.info(`AnnotatedSentence.generate, created segment ${chars} with annotations:`);
          // this.$log.info(annotationsForThisSegment);
          this.segments.push(annotatedSegment)
          this.segmentBoundaries.push(boundaryChars)
          chars = ''
          boundaryChars = ''
          segmentStartIndex = i
          characterAnnotations.splice(0, characterAnnotations.length)

          // not introducing a new segment, but we do still have
          // boundaryCharacters in the queue. so apparently those characters are
          // going to be part of the segment; copy them into the segment.
        } else if (boundaryChars) {
          for (let j = 0; j < boundaryChars.length; j += 1) {
            chars += boundaryChars[j]
            characterAnnotations.push(annotations[i - boundaryChars.length + j])
          }
          boundaryChars = ''
        }
        chars += text[i]
        characterAnnotations.push(annotations[i])
      }
    }

    // last segment
    this.segments.push(new AnnotatedSegment(this, this.segments.length, segmentStartIndex, chars, characterAnnotations))
    this.segmentBoundaries.push(null)
  }

  forEach(callback: (segment: AnnotatedSegment, segmentBoundary: string | null, index?: number) => void): void {
    this.segments.forEach((annotatedSegment, segmentIndex) => {
      callback(annotatedSegment, this.segmentBoundaries[segmentIndex], segmentIndex)
    })
  }
}
export { AnnotatedSentence }
